/**
 * EmptyState, ErrorState, LoadingState, Skeleton — the four "not the happy
 * path" surfaces.
 *
 * Used by: EVERY list and detail screen in the three apps. The house rule the
 * Admin app follows literally is: a screen must handle loading, empty, error
 * AND offline. A spinner that never resolves and a blank table look identical
 * to a user, and both get reported as "hệ thống hỏng".
 *
 * ErrorState is built for the API's typed errors: pass `code` from `ApiError`
 * and it is shown next to the message, so a support call starts with a code
 * instead of "nó báo lỗi".
 */
import type { ReactNode } from 'react';
import { cn } from '../utils.js';
import { Button } from './Button.js';

// ---------------------------------------------------------------------------

export interface EmptyStateProps {
  readonly title: string;
  readonly description?: ReactNode;
  readonly icon?: ReactNode;
  readonly action?: ReactNode;
  readonly className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps): React.JSX.Element {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <div aria-hidden="true" className="mb-3 text-4xl text-fg-subtle">
        {icon ?? '∅'}
      </div>
      <h3 className="text-base font-semibold text-fg">{title}</h3>
      {description !== undefined ? (
        <p className="mt-1 max-w-md text-sm text-fg-muted">{description}</p>
      ) : null}
      {action !== undefined ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface ErrorStateProps {
  readonly title?: string;
  /** The Vietnamese message from the API, or a local fallback. */
  readonly message?: string;
  /** `ApiError.code` — shown so support can act on it. */
  readonly code?: string | null;
  /** `ApiError.requestId` — the single most useful thing in a bug report. */
  readonly requestId?: string | null;
  readonly onRetry?: () => void | Promise<unknown>;
  readonly className?: string;
  readonly children?: ReactNode;
}

export function ErrorState({
  title = 'Không tải được dữ liệu',
  message,
  code,
  requestId,
  onRetry,
  className,
  children,
}: ErrorStateProps): React.JSX.Element {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg bg-danger-bg/50 px-6 py-10 text-center ring-1 ring-inset ring-danger-solid/20',
        className,
      )}
    >
      <div aria-hidden="true" className="text-3xl">
        ⚠️
      </div>
      <h3 className="text-base font-semibold text-fg">{title}</h3>
      {message !== undefined ? <p className="max-w-lg text-sm text-fg-muted">{message}</p> : null}
      {code !== undefined && code !== null ? (
        <p className="font-mono text-xs text-fg-subtle">
          Mã lỗi: {code}
          {requestId !== undefined && requestId !== null ? ` · Request ${requestId}` : ''}
        </p>
      ) : null}
      {children}
      {onRetry !== undefined ? (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-2">
          Thử lại
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface LoadingStateProps {
  readonly label?: string;
  readonly className?: string;
  readonly compact?: boolean;
}

export function LoadingState({
  label = 'Đang tải dữ liệu…',
  className,
  compact = false,
}: LoadingStateProps): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center justify-center gap-3 text-sm text-fg-muted',
        compact ? 'py-4' : 'py-12',
        className,
      )}
    >
      <svg className="h-5 w-5 animate-spin text-brand-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
        <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface SkeletonProps {
  readonly className?: string;
  /** Convenience: render N stacked bars for a list placeholder. */
  readonly lines?: number;
}

/**
 * Skeleton — a shimmering placeholder block.
 *
 * Preferred over a spinner when the shape of the result is known (a table, a
 * form), because the layout does not jump when the data lands.
 * `aria-hidden`: a screen reader should hear "đang tải" from LoadingState, not
 * a description of grey rectangles.
 */
export function Skeleton({ className, lines }: SkeletonProps): React.JSX.Element {
  if (lines !== undefined && lines > 0) {
    return (
      <div className="flex flex-col gap-2" aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton key={i} className={cn('h-4', i === lines - 1 && 'w-2/3', className)} />
        ))}
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className={cn('relative overflow-hidden rounded-md bg-surface-sunken', className ?? 'h-4 w-full')}
    >
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/25 to-transparent" />
    </div>
  );
}

/** Table-shaped skeleton, used while a list screen's first page loads. */
export function TableSkeleton({
  rows = 6,
  columns = 5,
}: {
  readonly rows?: number;
  readonly columns?: number;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 p-4" aria-hidden="true">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className={cn('h-5 flex-1', c === 0 && 'max-w-[10rem]')} />
          ))}
        </div>
      ))}
    </div>
  );
}
